<template>
  <div class="product-view">
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
      class="product-loader"
    />

    <div v-else>
      <div class="summary-row mb-4">
        <div class="summary-box">
          <div class="summary-label">Total Qty</div>
          <div class="summary-value">{{ totalQty }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Total Sales</div>
          <div class="summary-value">{{ totalPrice | currency }}</div>
        </div>

        <div v-if="!hideCost" class="summary-box">
          <div class="summary-label">Total Cost</div>
          <div class="summary-value">{{ totalCost | currency }}</div>
        </div>

        <div v-if="!hideProfit" class="summary-box">
          <div class="summary-label">Total Profit</div>
          <div class="summary-value">{{ totalProfit | currency }}</div>
        </div>
      </div>

      <v-data-table
        dense
        readonly
        :sort-by="sortBy"
        sort-desc
        :mobile-breakpoint="0"
        :headers="headers"
        :items-per-page="Product.length"
        hide-default-footer
        :expanded.sync="expanded"
        show-expand
        :items="Product"
        class="product-table"
      >
        <template v-slot:item="{ item, isExpanded, expand }">
          <tr class="product-row">
            <td class="text-left">
              <v-btn
                class="no-print"
                @click="expand(!isExpanded)"
                icon
                small
              >
                <v-icon>
                  {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
              </v-btn>
            </td>

            <td class="text-left text-capitalize">
              <v-icon v-if="item.trackSerialNumber" color="blue" small>
                mdi-barcode-scan
              </v-icon>

              <v-icon v-else color="teal" small>
                mdi-package-variant-closed
              </v-icon>

              <span class="ml-2 font-weight-bold">{{ item.name }}</span>
            </td>

            <td class="text-center">
              <span class="font-weight-bold">{{ item.totalQty }}</span>
              <span class="caption pink--text ml-1">
                {{ percentQty(item.totalQty) }}%
              </span>
            </td>

            <td class="text-right font-weight-bold">
              {{ item.totalPrice | currency }}
            </td>

            <td v-if="!hideCost" class="text-right">
              {{ item.totalCost | currency }}
            </td>

            <td
              v-if="!hideProfit"
              class="text-right font-weight-bold"
              :style="{ color: color(item) }"
            >
              {{ (item.totalPrice - item.totalCost) | currency }}
              <span class="green--text">
                / {{ profitPercent(item) }}%
              </span>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <v-card color="#f8fafc" flat>
              <v-card-text class="pa-3">
                <InvoiceDetail
                  :ProductId="item.id"
                  :CustomerId="CustomerId"
                  :start="start"
                  :end="end"
                  :hideProfit="hideProfit"
                  :hideCost="hideCost"
                />
              </v-card-text>
            </v-card>
          </td>
        </template>

        <template v-slot:body.append v-if="Product.length > 0">
          <tr class="table-total-row">
            <td></td>

            <td class="font-weight-bold">Total</td>

            <td class="text-center font-weight-bold">
              {{ totalQty }}
              <span class="caption pink--text">100%</span>
            </td>

            <td class="text-right font-weight-bold">
              {{ totalPrice | currency }}
            </td>

            <td v-if="!hideCost" class="text-right font-weight-bold">
              {{ totalCost | currency }}
            </td>

            <td v-if="!hideProfit" class="text-right font-weight-bold">
              {{ totalProfit | currency }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <div v-if="Product.length === 0" class="empty-state">
        No product sales found for this date range.
      </div>
    </div>
  </div>
</template>

<script>
import saleService from "@/modules/ReportView/service.js";

const InvoiceDetail = () =>
  import("@/modules/ReportView/GlobalReport/InvoiceDetail.vue");

export default {
  name: "global-product-report",

  props: [
    "CategoryId",
    "GroupId",
    "groupBy",
    "reset",
    "CustomerId",
    "hideProfit",
    "hideCost",
    "start",
    "end",
    "serializedItem",
  ],

  components: {
    InvoiceDetail,
  },

  data() {
    return {
      isLoading: true,
      Product: [],
      expanded: [],
      expenseExpanded: [],
      sortBy: "totalQty",
      headers: [
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
        },
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Qty",
          align: "center",
          value: "totalQty",
          sortable: true,
        },
        {
          text: "Sale",
          align: "right",
          value: "totalPrice",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    totalProfit() {
      return this.totalPrice - this.totalCost;
    },

    totalPrice() {
      return this.Product.reduce((sum, row) => {
        return sum + Number(row.totalPrice || 0);
      }, 0);
    },

    totalCost() {
      return this.Product.reduce((sum, row) => {
        return sum + Number(row.totalCost || 0);
      }, 0);
    },

    totalQty() {
      return this.Product.reduce((sum, row) => {
        return sum + Number(row.totalQty || 0);
      }, 0);
    },
  },

  watch: {
    reset() {
      this.getProduct();
    },
    CustomerId() {
      this.getProduct();
    },
    serializedItem() {
      this.getProduct();
    },
    start() {
      this.getProduct();
    },
    end() {
      this.getProduct();
    },
    CategoryId() {
      this.getProduct();
    },
    groupBy() {
      this.getProduct();
    },
    GroupId() {
      this.getProduct();
    },
  },

  async created() {
    this.buildHeaders();
    await this.getProduct();
  },

  methods: {
    buildHeaders() {
      if (!this.hideCost) {
        this.headers.push({
          text: "Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        });
      }

      if (!this.hideProfit) {
        this.headers.push({
          text: "Profit",
          align: "right",
          value: "totalProfit",
          sortable: true,
        });
      }
    },

    percentQty(qty) {
      if (!this.totalQty) {
        return "0.00";
      }

      return ((Number(qty || 0) / this.totalQty) * 100).toFixed(2);
    },

    profitPercent(item) {
      const cost = Number(item.totalCost || 0);
      const profit = Number(item.totalPrice || 0) - cost;

      if (!cost) {
        return "0.00";
      }

      return ((profit / cost) * 100).toFixed(2);
    },

    color(item) {
      const profit = Number(item.totalPrice || 0) - Number(item.totalCost || 0);
      return profit < 0 ? "red" : "black";
    },

    async getProduct() {
      this.isLoading = true;

      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;
      if (this.CustomerId) where.CustomerId = this.CustomerId;

      const groupBy = (this.groupBy || "").toLowerCase();

      if (!groupBy || groupBy === "category") {
        if (this.CategoryId) where.CategoryId = this.CategoryId;
      } else {
        where.groupBy = groupBy;

        if (this.GroupId === 0 || this.GroupId === "0") {
          where.notSelected = true;
        } else {
          where.GroupId = this.GroupId;
        }
      }

      if (this.serializedItem !== undefined) {
        where.serializedItem = this.serializedItem;
      }

      try {
        const response = await saleService.getProduct(where);
        this.Product = response.data || [];
      } catch (error) {
        console.error("getProduct failed", error);
        this.Product = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.product-view {
  background: #ffffff;
}

.product-loader {
  border-radius: 16px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.product-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.product-row:hover {
  background: #f8fafc;
}

.table-total-row {
  background: #f8fafc;
}

.empty-state {
  text-align: center;
  color: #64748b;
  font-size: 13px;
  padding: 28px 0 8px;
}

@media (max-width: 960px) {
  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .summary-row {
    grid-template-columns: 1fr;
  }
}

@media print {
  .no-print {
    display: none;
  }

  * {
    background: transparent !important;
    color: #000 !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }
}
</style>